import { Modal, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Avatar } from "@unicity/design-system";
import { useTranslateKey } from "../../hooks/useTranslate";

// GO-2842: view a member/partner profile avatar large. Rendered as a single
// scrim over the slide-out drawer column — NOT a screen-centered dialog with
// its own full-screen backdrop, which stacked a second scrim on top of the
// drawer's own (the "double overlay" UX review flagged). Matching the Figma
// (node 9076-11827): the enlarged avatar sits over the drawer, and the one
// scrim extends to cover the slide-out.
//
// Uses MUI Modal (focus trap + Escape/onClose + portal + aria) with its
// default backdrop hidden; the scrim is our own layer sized to the drawer
// column (right-anchored, same width as MembersDrawer/DistributorsDrawer), so
// the drawer's existing backdrop stays the single scrim on the left and no
// region is dimmed twice.
const LargeAvatarModal = ({ open, onClose, name, imageUrl }) => {
  const closeLabel = useTranslateKey("close");
  const dialogLabel = useTranslateKey("profile_photo");
  return (
    <Modal
      open={open}
      onClose={onClose}
      hideBackdrop
      aria-label={dialogLabel}
      data-test-id="large-avatar-modal"
      sx={{
        // Cover only the drawer column (right-anchored), matching the Drawer's
        // width (100vw on xs, capped at 400 on sm+). The drawer's own backdrop
        // remains the single scrim on the left — no double-dim.
        position: "fixed",
        top: 0,
        bottom: 0,
        right: 0,
        left: "auto",
        width: { xs: "100vw", sm: "400px" },
        zIndex: (theme) => theme.zIndex.modal + 1,
      }}
    >
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          pt: 8,
          px: 2,
          outline: "none",
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={dialogLabel}
          sx={{
            backgroundColor: "#fff",
            borderRadius: "20px",
            padding: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            boxShadow: 6,
            width: "100%",
            maxWidth: 320,
          }}
        >
          <IconButton
            onClick={onClose}
            data-test-id="large-avatar-close-button"
            aria-label={closeLabel}
            sx={{ alignSelf: "flex-start", padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
          <Avatar
            id="large-avatar"
            alt={name}
            src={imageUrl}
            sx={{ width: 240, height: 240, fontSize: "96px" }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export { LargeAvatarModal };
